import React from 'react';
import { Button, Card, Form, Input, Modal, Space, Tabs, Tooltip } from 'antd';
import styles from './profile-change-info.module.scss';
import './my-profile.scss';
import { AppContext } from '@layouts/AppRouter';
import { getUserRoleByRoles, validatePassword } from '@services/utils';
import { toast } from 'react-toastify';
import api from '@services/api';
import { ApiError, User, SuperGroup } from '@services/types';
import { Role } from '@openapi';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getPasswordRequirementsTooltip } from '@services/passwordRecommendation';

const { TabPane } = Tabs;

const MyProfile = () => {
  const [form] = Form.useForm<User>();
  const [superGroups, setSuperGroups] = React.useState<SuperGroup[]>([]);

  const { user, setUser, variablesProfile } = React.useContext(AppContext);
  const [activeTab, setActiveTab] = React.useState('info');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  React.useEffect(() => {
    api.administration.getSuperGroups().then(setSuperGroups);
  }, []);

  const handleSubmit = async () => {
    try {
      if (!user) {
        throw new Error('User is not available');
      }

      const values = await form.validateFields();
      console.log('🚀 ~ file: MyProfile.tsx ~ line 36 ~ handleSubmit ~ user', user);
      await api.administration.updateUser(user.id, {
        ...values,
        role: getUserRoleByRoles(user.roles || []) || Role.Superadmin,
        email: user.email,
        superGroupIds: superGroups.map((superGroup) => superGroup.id),
      });
      toast.success('User was updated successfully');
      const newUser = await api.auth.me();
      setUser(newUser);
      form.resetFields(['password', 'confirm']);
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(`${error.error.details}`);
      } else {
        toast.error('User was not updated/created due to error');
      }
      console.log('CreateUserModal ~ handleSubmit ~ error', error);
    }
  };

  return (
    <Card
      title="My Profile"
      extra={
        <Space>
          <Button type="primary" onClick={handleSubmit}>
            Modify
          </Button>
        </Space>
      }
    >
      <div className="main">
        <div className="modal-profile-window">
          <div className="modal-container">
            <div className="role-block">
              <div className="user-info">
                <span className="title">My Email</span>
                <span>{user?.email}</span>
              </div>
              <div className="user-info">
                <span className="title">Role</span>
                <span>{getUserRoleByRoles(user?.roles || [])}</span>
              </div>
              <div className="user-info">
                <span className="title">Perimeter</span>
                <span>{user?.etc?.perimeterNames?.join(', ')}</span>
              </div>
            </div>
            <Tabs
              onChange={handleTabChange}
              style={{ marginTop: '10px' }}
              type="card"
              size="large"
              defaultActiveKey={'info' || 'security'}
            >
              <TabPane tab={user ? 'Info' : 'Add Info'} key="info">
                <Form
                  className={styles.form}
                  layout="vertical"
                  form={form}
                  name="modify"
                  initialValues={{
                    firstName: user?.firstName || '',
                    lastName: user?.lastName || '',
                  }}
                  scrollToFirstError
                >
                  <Form.Item
                    className={styles.block}
                    style={{ gridArea: 'firstName' }}
                    label="First Name"
                    name="firstName"
                  >
                    <Input className={styles.item} placeholder="First Name" />
                  </Form.Item>
                  <Form.Item
                    className={styles.block}
                    style={{ gridArea: 'lastName' }}
                    label="Last Name"
                    name="lastName"
                  >
                    <Input className={styles.item} placeholder="Last Name" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane tab="Security" key="security">
                {activeTab === 'security' && (
                  <Form
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '35px' }}
                    form={form}
                    layout="vertical"
                    name="modify"
                    initialValues={{
                      password: '',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr max-content',
                        gridGap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your password!',
                          },
                          () => ({
                            validator(_, value) {
                              const error = validatePassword(value, variablesProfile?.minPasswordSize);
                              if (error) {
                                return Promise.reject(new Error(error));
                              } else {
                                return Promise.resolve();
                              }
                            },
                          }),
                        ]}
                        hasFeedback
                      >
                        <Input.Password />
                      </Form.Item>
                      <Tooltip
                        placement="right"
                        title={getPasswordRequirementsTooltip(variablesProfile?.minPasswordSize)}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>

                    <Form.Item
                      name="confirm"
                      label="Confirm Password"
                      dependencies={['password']}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: 'Please confirm your password!',
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }

                            return Promise.reject(new Error('The two passwords that you entered do not match!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                  </Form>
                )}
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default React.memo(MyProfile);
